/**
 * Navigation Store Action Creators
 * Factory functions that create store actions with access to set/get
 */

import type { StoreApi } from 'zustand'

import { computeExpandedFromPath, joinExpandedItems, joinPath } from './helpers'
import type { NavigationActions, NavigationStore } from './types'

type SetState = StoreApi<NavigationStore>['setState']
type GetState = StoreApi<NavigationStore>['getState']

/**
 * Create path navigation actions
 */
const createPathActions = (
  set: SetState
): Pick<NavigationActions, 'navigateTo' | 'setPath' | 'resetNavigationFlag'> => ({
  navigateTo: (path: string[]) => {
    const expandedFromPath = computeExpandedFromPath(path)
    const isAtMethodLevel = path.length === 3
    const methodId = path[2]
    const newAccordions = isAtMethodLevel && methodId ? [methodId] : []

    set({
      path,
      pathString: joinPath(path),
      expandedItems: expandedFromPath,
      expandedItemsString: joinExpandedItems(expandedFromPath),
      openAccordions: newAccordions,
      openAccordionsString: joinExpandedItems(newAccordions),
      pathChangedByNavigation: true,
    })
  },

  setPath: (path: string[]) => {
    set({
      path,
      pathString: joinPath(path),
      pathChangedByNavigation: false,
    })
  },

  resetNavigationFlag: () => {
    set({
      pathChangedByNavigation: false,
    })
  },
})

/**
 * Create tree expansion actions
 */
const createTreeActions = (
  set: SetState,
  get: GetState
): Pick<NavigationActions, 'toggleExpanded' | 'expandItem' | 'collapseItem' | 'setExpandedItems'> => ({
  toggleExpanded: (itemId: string) => {
    const { expandedItems } = get()
    const isExpanded = expandedItems.includes(itemId)

    const newExpanded = isExpanded
      ? expandedItems.filter((id) => id !== itemId && !id.startsWith(itemId + '/'))
      : [...expandedItems, itemId]

    set({
      expandedItems: newExpanded,
      expandedItemsString: joinExpandedItems(newExpanded),
    })
  },

  expandItem: (itemId: string) => {
    const { expandedItems } = get()
    if (!expandedItems.includes(itemId)) {
      const newExpanded = [...expandedItems, itemId]
      set({
        expandedItems: newExpanded,
        expandedItemsString: joinExpandedItems(newExpanded),
      })
    }
  },

  collapseItem: (itemId: string) => {
    const { expandedItems } = get()
    const newExpanded = expandedItems.filter((id) => id !== itemId && !id.startsWith(itemId + '/'))
    set({
      expandedItems: newExpanded,
      expandedItemsString: joinExpandedItems(newExpanded),
    })
  },

  setExpandedItems: (items: string[]) => {
    set({
      expandedItems: items,
      expandedItemsString: joinExpandedItems(items),
    })
  },
})

/**
 * Create accordion actions
 */
const createAccordionActions = (
  set: SetState,
  get: GetState
): Pick<NavigationActions, 'toggleAccordion' | 'openAccordion' | 'closeAccordion'> => ({
  toggleAccordion: (itemId: string) => {
    const { openAccordions } = get()
    const isOpen = openAccordions.includes(itemId)

    if (isOpen) {
      const newAccordions = openAccordions.filter((id) => id !== itemId)
      set({
        openAccordions: newAccordions,
        openAccordionsString: joinExpandedItems(newAccordions),
        pathChangedByNavigation: false,
      })
    } else {
      const newAccordions = [...openAccordions, itemId]
      set({
        openAccordions: newAccordions,
        openAccordionsString: joinExpandedItems(newAccordions),
        pathChangedByNavigation: false,
      })
    }
  },

  openAccordion: (itemId: string) => {
    const { openAccordions } = get()
    if (!openAccordions.includes(itemId)) {
      const newAccordions = [...openAccordions, itemId]
      set({
        openAccordions: newAccordions,
        openAccordionsString: joinExpandedItems(newAccordions),
      })
    }
  },

  closeAccordion: (itemId: string) => {
    const { openAccordions } = get()
    const newAccordions = openAccordions.filter((id) => id !== itemId)
    set({
      openAccordions: newAccordions,
      openAccordionsString: joinExpandedItems(newAccordions),
    })
  },
})

/**
 * Create URL synchronization action
 */
const createUrlSyncAction = (set: SetState): Pick<NavigationActions, 'syncFromUrl'> => ({
  syncFromUrl: (hash: string) => {
    const path = hash ? hash.split('/') : []
    const expandedFromPath = computeExpandedFromPath(path)
    const isAtMethodLevel = path.length === 3
    const methodId = path[2]
    const newAccordions = isAtMethodLevel && methodId ? [methodId] : []

    set({
      path,
      pathString: joinPath(path),
      expandedItems: expandedFromPath,
      expandedItemsString: joinExpandedItems(expandedFromPath),
      openAccordions: newAccordions,
      openAccordionsString: joinExpandedItems(newAccordions),
      pathChangedByNavigation: true,
    })
  },
})

/**
 * Create all navigation actions
 */
export const createNavigationActions = (set: SetState, get: GetState): NavigationActions => ({
  ...createPathActions(set),
  ...createTreeActions(set, get),
  ...createAccordionActions(set, get),
  ...createUrlSyncAction(set),
})
